import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSetting } from './useSetting'
import { getTickerBarsWindow } from '../api/marketData'
import { ORB_BLOCK_KEY, orbPhase, orbWindowToday, type OrbPhase } from '../lib/orbRule'

export interface OrbBlockStatus {
  /** True when the rule is on AND opens should be blocked right now. */
  active: boolean
  /** Short clause for tooltip/banner; null when inactive. */
  reason: string | null
}

function fmtPrice(n: number): string {
  return `$${n.toFixed(2)}`
}

/**
 * "Block opens while price is inside the ORB" rule. RTH-only:
 *  - Pre-09:30 ET / weekend / post-16:00 ET: inactive.
 *  - 09:30–09:45 ET (OR forming): always block.
 *  - After 09:45 ET: block when orbLow ≤ last ≤ orbHigh; otherwise allow.
 * Closes are always allowed — this hook only feeds the BUY-disabled path.
 */
export function useOrbBlock(symbol: string | null | undefined, last: number | null): OrbBlockStatus {
  const [enabled] = useSetting<boolean>(ORB_BLOCK_KEY, false)
  const [phase, setPhase] = useState<OrbPhase>(() => orbPhase())

  useEffect(() => {
    if (!enabled) return
    const id = window.setInterval(() => setPhase(orbPhase()), 30_000)
    return () => window.clearInterval(id)
  }, [enabled])

  // Today's OR window in UTC ms — recomputed every render is fine, it's pure.
  const orWindow = orbWindowToday()
  const fetchEnabled =
    !!enabled && !!symbol && (phase === 'forming' || phase === 'finalized')

  const orbQ = useQuery({
    queryKey: ['orb-window', symbol, orWindow.startMs, orWindow.endMs],
    queryFn: () => getTickerBarsWindow(symbol!, orWindow.startMs, orWindow.endMs, '1m'),
    enabled: fetchEnabled && phase === 'finalized',
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  })

  if (!enabled) return { active: false, reason: null }

  if (phase === 'forming') {
    return { active: true, reason: 'Inside ORB (09:30–09:45 ET)' }
  }

  if (phase !== 'finalized') {
    return { active: false, reason: null }
  }

  const bars = orbQ.data?.bars
  if (!bars || bars.length === 0) {
    // No bars yet (fresh fetch, broker delay, illiquid symbol): don't block.
    // Better to let a trade through than to block on missing data.
    return { active: false, reason: null }
  }

  let high = -Infinity
  let low = Infinity
  for (const b of bars) {
    if (b.h > high) high = b.h
    if (b.l < low) low = b.l
  }
  if (!isFinite(high) || !isFinite(low)) {
    return { active: false, reason: null }
  }

  if (last == null) return { active: false, reason: null }

  if (last >= low && last <= high) {
    return {
      active: true,
      reason: `Inside ORB (${fmtPrice(low)}–${fmtPrice(high)})`,
    }
  }

  return { active: false, reason: null }
}
